import React from "react";
// UI Framework
import { toast } from "react-toastify";
// Common Components
import {
  TextInput,
  Button,
  SelectableButtons,
  DynamicMultiSelectDropdown,
  DynamicDropdown,
  StaticDropdown,
} from "src/Components";
// Hooks
import { useAppSelector } from "src/App/hooks";
// Requests
import { getStepInstruments } from "src/Requests/stepRequests";
import { getBaseInstruments } from "src/Requests/Order/tradeTableRequests";
import {
  copyFixedBasePriceFilterSettings,
  setFilterSetting,
  getFilterSetting,
  getBaseTypeDropdown,
  getPairType,
} from "src/Requests/coveredCall";
// Types
import { IFilterParams } from "src/Interfaces/coveredCall";
import { ExchangeType } from "src/Interfaces/connections";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
// Utilities
import * as R from "ramda";
import { packetErrorHandler } from "src/Utils/requestUtils";

const exchangeTypeOption: DropdownItem[] = [
  { id: ExchangeType.StockExchange, name: "اوراق بهادار" },
  { id: ExchangeType.CommodityExchange, name: "کالا" },
];

const fixedBasePriceExcludedFilterKeys: (keyof IFilterParams)[] = [
  "minCallITMPercentSide1",
  "maxCallITMPercentSide1",
  "minPutITMPercentSide1",
  "maxPutITMPercentSide1",
  "minBuysideRemainingDay",
  "maxBuysideRemainingDay",
  "minCallDTMSide1",
  "maxCallDTMSide1",
  "minPutDTMSide1",
  "maxPutDTMSide1",
];

const normalizeFixedBasePriceFilter = (
  filter: IFilterParams,
): IFilterParams => ({
  ...(R.omit(fixedBasePriceExcludedFilterKeys, filter) as IFilterParams),
  pairType: ["7"],
});

interface IProps {
  onClose: () => void;
  onApplied?: () => void;
  useFixedBasePrices?: boolean;
  initialRiskProfileId?: string;
  onCopied?: () => void;
}
const FilterSettingModal: React.FC<IProps> = ({
  onClose,
  onApplied,
  useFixedBasePrices = false,
  initialRiskProfileId,
  onCopied,
}) => {
  // store
  const riskProfiles = useAppSelector(
    (store) => store.coveredCall.riskProfiles,
  );
  // state
  const [loading, setLoading] = React.useState(false);
  const [copying, setCopying] = React.useState(false);
  const [riskProfileId, setRiskProfileIds] = React.useState<string>(
    initialRiskProfileId ?? riskProfiles[0]?.id?.toString() ?? "0",
  );
  const [searchParam, setSearchParam] = React.useState<IFilterParams>(
    {} as IFilterParams,
  );

  React.useEffect(() => {
    getFilterSetting(riskProfileId, useFixedBasePrices)
      .then((resp) => {
        setSearchParam(
          useFixedBasePrices ? normalizeFixedBasePriceFilter(resp) : resp,
        );
      })
      .catch(packetErrorHandler);
  }, [riskProfileId, useFixedBasePrices]);

  const searchParamChangeHandler = (name: string, value: any) => {
    setSearchParam({
      ...searchParam,
      [name]: value,
    });
  };

  const handleSearch = () => {
    setLoading(true);
    const submittedSearchParam = useFixedBasePrices
      ? normalizeFixedBasePriceFilter(searchParam)
      : searchParam;
    const cleanObject = R.pickBy(
      (v) => !(v === null || v === "" || (Array.isArray(v) && v.length === 0)),
      submittedSearchParam,
    );
    setFilterSetting(
      cleanObject as IFilterParams,
      riskProfileId,
      useFixedBasePrices,
    )
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          if (onApplied) onApplied();
          onClose();
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const copyFromEntryPosition = () => {
    const numericRiskProfileId = Number(riskProfileId);
    if (
      riskProfileId == null ||
      riskProfileId === "" ||
      !Number.isInteger(numericRiskProfileId) ||
      numericRiskProfileId < 0
    ) {
      toast.error("ریسک پروفایل انتخاب نشده است");
      return;
    }

    setCopying(true);
    copyFixedBasePriceFilterSettings(riskProfileId)
      .then(async (resp) => {
        if (!resp.data) {
          toast.error(resp.msg);
          return;
        }

        const copiedFilter = await getFilterSetting(riskProfileId, true);
        setSearchParam(normalizeFixedBasePriceFilter(copiedFilter));
        toast.success(resp.msg);
        onCopied?.();
      })
      .catch(packetErrorHandler)
      .finally(() => setCopying(false));
  };

  return (
    <div className="flex flex-col justify-center items-center">
      <div className="border border-gray-300 rounded p-3 flex flex-col gap-3 !w-full !min-w-full">
        <SelectableButtons
          label="ریسک پروفایل"
          name="riskProfileId"
          onChange={(_: string, value: string) => setRiskProfileIds(value)}
          options={riskProfiles}
          value={riskProfileId}
          hideSelectAll
        />
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-1/2">
            <DynamicMultiSelectDropdown
              dropdown={{
                name: "baseInstrument",
                placeholder: "نماد پایه",
                extraLabel: true,
              }}
              getData={() => getBaseInstruments()}
              onChange={searchParamChangeHandler}
              value={searchParam?.baseInstrument}
            />
          </div>
          <div className="w-1/2">
            <StaticDropdown
              dropdown={{
                name: "exchangeType",
                placeholder: "بورس",
                extraLabel: true,
              }}
              onChange={searchParamChangeHandler}
              options={exchangeTypeOption}
              value={searchParam?.exchangeType?.toString() || ""}
            />
          </div>
        </div>

        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-1/2">
            {useFixedBasePrices ? (
              <div className="flex flex-col gap-1">
                <div className="text-[10px]">نوع جفت</div>
                <div className="rounded border border-gray-300 bg-gray-50 px-3 py-2 text-xs">
                  کاورد کال
                </div>
              </div>
            ) : (
              <DynamicMultiSelectDropdown
                dropdown={{
                  name: "pairType",
                  placeholder: "نوع جفت",
                  extraLabel: true,
                }}
                getData={getPairType}
                onChange={searchParamChangeHandler}
                value={searchParam?.pairType}
              />
            )}
          </div>
          <div className="w-1/2">
            <DynamicDropdown
              dropdown={{
                name: "baseType",
                placeholder: "نوع پایه",
                extraLabel: true,
              }}
              getData={getBaseTypeDropdown}
              onChange={searchParamChangeHandler}
              value={searchParam?.baseType}
            />
          </div>
        </div>
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <DynamicDropdown
              dropdown={{
                name: "buySideInstrument",
                placeholder: "نماد سمت خرید",
                extraLabel: true,
              }}
              getData={getStepInstruments}
              onChange={searchParamChangeHandler}
              value={searchParam?.buySideInstrument?.toString() || ""}
            />
          </div>
          <div className="w-full">
            <DynamicDropdown
              dropdown={{
                name: "sellSideInstrument",
                placeholder: "نماد سمت فروش",
                extraLabel: true,
              }}
              getData={getStepInstruments}
              onChange={searchParamChangeHandler}
              value={searchParam?.sellSideInstrument?.toString() || ""}
            />
          </div>
        </div>

        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <TextInput
              name="minYesterdayValue"
              label="حداقل ارزش معاملاتی روز قبل"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.minYesterdayValue?.toString() || ""}
              kind="price"
            />
          </div>
          <div className="w-full">
            <TextInput
              name="minLastTradeValue"
              label="حداقل حجم معاملات روز قبل"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.minLastTradeValue?.toString() || ""}
              kind="price"
            />
          </div>
        </div>

        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-1/2">
            <TextInput
              name="minDailyProfit"
              label="حداقل درصد بازده روزانه"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.minDailyProfit?.toString() || ""}
              kind="negativeDecimalNumbers"
            />
          </div>

          <div className="w-1/2">
            <TextInput
              name="maxDailyOffestProfit"
              label="حداکثر درصد بازده آفست روزانه"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.maxDailyOffestProfit?.toString() || ""}
              kind="negativeDecimalNumbers"
            />
          </div>
        </div>
        {!useFixedBasePrices ? (
          <>
            <div className="flex flex-row justify-start items-start gap-3 w-full">
              <div className="w-full">
                <TextInput
                  name="minCallITMPercentSide1"
                  label="حداقل درصد ITM کال سمت خرید"
                  extraLabel
                  onChange={searchParamChangeHandler}
                  value={searchParam?.minCallITMPercentSide1?.toString() || ""}
                  kind="negativeDecimalNumbers"
                />
              </div>
              <div className="w-full">
                <TextInput
                  name="maxCallITMPercentSide1"
                  label="حداکثر درصد ITM کال سمت خرید"
                  extraLabel
                  onChange={searchParamChangeHandler}
                  value={searchParam?.maxCallITMPercentSide1?.toString() || ""}
                  kind="negativeDecimalNumbers"
                />
              </div>
            </div>
            <div className="flex flex-row justify-start items-start gap-3 w-full">
              <div className="w-full">
                <TextInput
                  name="minPutITMPercentSide1"
                  label="حداقل درصد ITM پوت سمت خرید"
                  extraLabel
                  onChange={searchParamChangeHandler}
                  value={searchParam?.minPutITMPercentSide1?.toString() || ""}
                  kind="negativeDecimalNumbers"
                />
              </div>
              <div className="w-full">
                <TextInput
                  name="maxPutITMPercentSide1"
                  label="حداکثر درصد ITM پوت سمت خرید"
                  extraLabel
                  onChange={searchParamChangeHandler}
                  value={searchParam?.maxPutITMPercentSide1?.toString() || ""}
                  kind="negativeDecimalNumbers"
                />
              </div>
            </div>
          </>
        ) : null}

        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-1/2">
            <TextInput
              name="minCallITMPercentSide2"
              label="حداقل درصد ITM کال سمت فروش"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.minCallITMPercentSide2?.toString() || ""}
              kind="negativeDecimalNumbers"
            />
          </div>
          <div className="w-1/2">
            <TextInput
              name="positionBreakEvenDiffPercent1"
              label="حداقل % فاصله تا نقطه سر به سر"
              extraLabel
              onChange={searchParamChangeHandler}
              value={
                searchParam?.positionBreakEvenDiffPercent1?.toString() || ""
              }
              kind="negativeDecimalNumbers"
            />
          </div>
        </div>
        {!useFixedBasePrices ? (
          <div className="flex flex-row justify-start items-start gap-3 w-full">
            <div className="w-full">
              <TextInput
                name="minBuysideRemainingDay"
                label="حداقل DTM خرید"
                extraLabel
                onChange={searchParamChangeHandler}
                value={searchParam?.minBuysideRemainingDay?.toString() || ""}
                kind="number"
              />
            </div>
            <div className="w-full">
              <TextInput
                name="maxBuysideRemainingDay"
                label="حداکثر DTM خرید"
                extraLabel
                onChange={searchParamChangeHandler}
                value={searchParam?.maxBuysideRemainingDay?.toString() || ""}
                kind="number"
              />
            </div>
          </div>
        ) : null}
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <TextInput
              label="حداقل DTM فروش"
              onChange={searchParamChangeHandler}
              name="minSellsideRemainingDay"
              value={searchParam.minSellsideRemainingDay?.toString()}
              extraLabel
              kind="number"
            />
          </div>
          <div className="w-full">
            <TextInput
              name="maxSellsideRemainingDay"
              label="حداکثر DTM فروش"
              extraLabel
              kind="number"
              onChange={searchParamChangeHandler}
              value={searchParam?.maxSellsideRemainingDay?.toString()}
            />
          </div>
        </div>
        {!useFixedBasePrices ? (
          <>
            <div className="flex flex-row justify-start items-start gap-3 w-full">
              <div className="w-full">
                <TextInput
                  name="minCallDTMSide1"
                  label="حداقل DTM کال سمت خرید"
                  extraLabel
                  onChange={searchParamChangeHandler}
                  value={searchParam?.minCallDTMSide1?.toString() || ""}
                  kind="number"
                />
              </div>
              <div className="w-full">
                <TextInput
                  name="maxCallDTMSide1"
                  label="حداکثر DTM کال سمت خرید"
                  extraLabel
                  onChange={searchParamChangeHandler}
                  value={searchParam?.maxCallDTMSide1?.toString() || ""}
                  kind="number"
                />
              </div>
            </div>
            <div className="flex flex-row justify-start items-start gap-3 w-full">
              <div className="w-full">
                <TextInput
                  name="minPutDTMSide1"
                  label="حداقل DTM پوت سمت خرید"
                  extraLabel
                  onChange={searchParamChangeHandler}
                  value={searchParam?.minPutDTMSide1?.toString() || ""}
                  kind="number"
                />
              </div>
              <div className="w-full">
                <TextInput
                  name="maxPutDTMSide1"
                  label="حداکثر DTM پوت سمت خرید"
                  extraLabel
                  onChange={searchParamChangeHandler}
                  value={searchParam?.maxPutDTMSide1?.toString() || ""}
                  kind="number"
                />
              </div>
            </div>
          </>
        ) : null}
      </div>
      <div className="mt-3 flex w-full justify-center gap-3">
        {useFixedBasePrices ? (
          <div className="w-64">
            <Button
              onClick={copyFromEntryPosition}
              text="کپی از ورود به پوزیشن"
              variant="outlined"
              loading={copying}
              disabled={loading || copying}
            />
          </div>
        ) : null}
        <div className="w-64">
          <Button
            onClick={handleSearch}
            text="ثبت"
            variant="contained"
            loading={loading}
            disabled={loading || copying}
          />
        </div>
      </div>
    </div>
  );
};

export default FilterSettingModal;
